/**
 * @file 学びメモに書き足した文 (学習者 × メモ × 日) のクライアント。
 * @what: `GET /families/{fid}/study/plans/{personId}/note_entries?from=&to=` (期間の一覧) と、
 *   `POST .../note_entries/{noteId}` (1回の保存ぶんの増分の足し込み)。
 * @why: 学びメモは1件の本文を上書きして保存するので、「どの日に何を書いたか」が残らない。本だなの
 *   「さいきん書いたメモ」は日 × 本で並べてその日に書いた文を出すため、保存のたびに足したぶんを
 *   別の器へ残す (代表の判断、2026-09-13)。送るのは「開いてから足したぶん」の全体で、同じ開き方からの
 *   送信はサーバが置き換える。※なぜなら自動保存は打鍵の途中でも走るので、直前の保存との差を足すと
 *   書きかけの行が段階ごとに残るためである。
 * @failure: ApiError を伝播する。entries が空 = その期間に何も書いていない、は正常系である。
 */

import { apiFetch } from "@/shared/api/client";

export interface StudyNoteEntry {
  note_id: string;
  /** 端末のローカル日付 (YYYY-MM-DD)。 */
  date: string;
  /** 最後に書いたときのメモの題である。 */
  title: string;
  /** 最後に書いたときのメモの紐づけ先の資料 (重複なし)。 */
  source_ids: string[];
  /** この日に書き足した文 (行を改行でつないだもの)。 */
  text: string;
  /** この日に足した画像 (手書きを含む) の数である。 */
  images: number;
  first_at: string;
  last_at: string;
}

/** 1回の保存で送るもの (メモを開いてから足したぶん)。同じ segment_id の前の送信は置き換わる。 */
export interface StudyNoteEntryDelta {
  date: string;
  title?: string;
  source_ids?: string[];
  /** 開き方の id で、メモを開くたびに付ける。 */
  segment_id: string;
  text: string;
  images: number;
}

function path(familyId: string, personId: string): string {
  return `/families/${encodeURIComponent(familyId)}/study/plans/${encodeURIComponent(
    personId,
  )}/note_entries`;
}

export async function fetchNoteEntries(
  familyId: string,
  personId: string,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<StudyNoteEntry[]> {
  const query = `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
  const res = await apiFetch<{ entries: StudyNoteEntry[] }>(
    `${path(familyId, personId)}${query}`,
    signal ? { signal } : {},
  );
  return res.entries;
}

export async function postNoteEntry(
  familyId: string,
  personId: string,
  noteId: string,
  delta: StudyNoteEntryDelta,
): Promise<StudyNoteEntry> {
  return apiFetch<StudyNoteEntry>(`${path(familyId, personId)}/${encodeURIComponent(noteId)}`, {
    method: "POST",
    body: delta,
  });
}
